import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, FormProvider, useForm } from "react-hook-form";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import LoadingButton from "@mui/lab/LoadingButton";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SelectChangeEvent } from "@mui/material/Select";
import Divider from "@mui/material/Divider/Divider";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { isEmpty, uniqBy } from "lodash";

import CustomTextField from "../../components/FormFields/CustomTextField";
import CustomCheckbox from "../../components/FormFields/CustomCheckbox";
import CustomSelect, {
  IOptions,
} from "../../components/FormFields/CustomSelect";
import BreadCrumb from "../../components/Breadcrumbs/Breadcrumbs";
import axios from "../../utils/axios";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { addAccount, updateAccount } from "../../store/accountThunk";
import { switchAccount } from '../../store/authSlice';
import { ApiStatus } from "../../utils/api.types";

import {
  IAccount,
  IAccountFormInputs,
  ISubscriptions,
} from "../../utils/account.types";

// Schema to validate account form.
const schema = yup.object().shape({
  name: yup.string().required("Name is required."),
  legalName: yup.string().required("Legal Name is required."),
  isActive: yup.boolean(),
  subscription: yup.string().required("Subscription is required."),
  location: yup
    .object()
    .required()
    .shape({
      address: yup.string().required("Address is required."),
      country: yup.string().required("Country is required."),
      city: yup.string().required("City is required."),
      zip: yup.string().required("Zip is required."),
    }),
  owner: yup
    .object()
    .required()
    .shape({
      name: yup.string().required("Owner name is required."),
      email: yup.string().required("Owner email is required."),
    }),
});

/**
 * Account Form Component.
 */
const AccountForm: FC = () => {
  const { accountId = "" } = useParams();
  const isEditing = accountId ? true : false;
  // use form
  const methods = useForm<IAccountFormInputs>({
    resolver: yupResolver(schema),
  });

  // States
  const [subscriptionOptions, setSubscriptionOptions] = useState<IOptions[]>(
    []
  );
  const [selectedSubscription, setSelectedSubscription] = useState("");
  const [isActive, setIsActive] = useState(true);

  const [locationData, setLocationData] = useState<any>([]);
  const [countries, setCountries] = useState<any>([]);
  const [selectedCountry, setSelectedCountry] = useState("");

  const [cities, setCities] = useState<any>([]);
  const [selectedCity, setselectedCity] = useState("");

  const [editAccountData, setEditAccountData] = useState(
    {} as IAccountFormInputs
  );
  const { status, accounts } = useAppSelector((state) => state.account);
  const { user, selectedAccount } = useAppSelector(state => state.auth);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  /**
   * Method to handle account form submit.
   */
  const onSubmit = async (data: IAccountFormInputs) => {
    // Dispatch add account action.
    try {
      if (!isEditing) {
        await dispatch(addAccount(data)).unwrap().then((data) => {
          if(data.account) {
            let selectId = selectedAccount._id
  
            if(selectId) {
              dispatch(switchAccount({ accountId: selectId, userId: user._id }));
            }
          }
        });
      } else {
        await dispatch(
          updateAccount({ accountPayload: data, id: accountId })
        ).unwrap();
      }
      navigate("/account");
    } catch (error: any) {
      // Empty catch block.
    }
  };

  /**
   * Method to handle Cancel button click.
   */
  const onCancel = () => {
    navigate("/account");
  };

  /**
   * Set edit account data in case of edit.
   */
  useEffect(() => {
    if (accountId) {
      const account = accounts.find(
        (account) => account._id === accountId
      ) as IAccount;

      if (account) {
        const { name, legalName, isActive, subscription, location, owner } =
          account;

        // const editData = {
        //   name,
        //   legalName,
        //   isActive,
        //   subscription: subscription._id,
        //   location,
        //   owner,
        // };
        // setEditAccountData(editData);
      }
    }
  }, [accountId, accounts]);

  /**
   * Fetch Subscriptions on Account form load.
   */
  useEffect(() => {
    let isMounted = true;
    // Method to fetch available subscriptions.
    const fetchSubscriptions = async () => {
      try {
        const response = await axios.get<ISubscriptions>(
          "/api/v1/subscription"
        );
        const { subscriptions = [] } = response.data;
        const options = subscriptions.map(({ _id, plan }) => ({
          label: plan.toUpperCase(),
          value: _id,
        }));
        if (isMounted) {
          setSubscriptionOptions(options);
        }
      } catch (error: any) {
        // Empty catch block.
      }
    };
    fetchSubscriptions();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      const response = await axios.get<any>(
        "https://countriesnow.space/api/v0.1/countries"
      );
      if (active) {
        const uniqCountries = uniqBy(response.data.data, "country");
        setLocationData(uniqCountries);
        const mappedCountries = uniqCountries.map((c: any) => c.country);
        setCountries(mappedCountries);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Set subscription and isActive fields in case of edit.
  useEffect(() => {
    if (isEditing && !isEmpty(editAccountData)) {
      setSelectedSubscription(editAccountData.subscription);
      setIsActive(editAccountData.isActive);
      // Set form data with default values in case of edit.
      methods.reset(editAccountData);
    }
  }, [isEditing, editAccountData, methods]);

  return (
    <>
      <BreadCrumb />
      <Container component="main">
        <CssBaseline />
        <Paper variant="elevation" elevation={2} sx={{ padding: 3, mt: 2 }}>
          <Typography component="h1" variant="h5" textAlign={"center"}>
            {isEditing ? "Edit Account" : "Create An Account"}
          </Typography>
          <FormProvider {...methods}>
            <Box
              component="form"
              onSubmit={methods.handleSubmit(onSubmit)}
              noValidate
              sx={{ mt: 1 }}
            >
              {/* Account Details */}
              <Grid container columnSpacing={1}>
                <Grid item md={6} xs={12}>
                  <CustomTextField label="Account Name" name="name" required />
                </Grid>
                <Grid item md={6} xs={12}>
                  <CustomTextField label="Legal Name" name="legalName" required />
                </Grid>
              </Grid>
              {isEditing && (
                <CustomCheckbox
                  label="Is Active"
                  name="isActive"
                  checked={isActive ?? false}
                  onChange={(event, checked) => {
                    setIsActive(checked);
                  }}
                />
              )}
              <CustomSelect
                name="subscription"
                label="Subscription"
                id="subscription"
                options={subscriptionOptions}
                value={selectedSubscription ?? ""}
                onChange={(event: SelectChangeEvent<any>) => {
                  setSelectedSubscription(event.target.value);
                }}
              />
              <Divider sx={{ mt: 2 }}> Owner Details </Divider>
              <CustomTextField
                label="Owner Name"
                name="owner.name"
                required
                disabled={isEditing}
              />
              <CustomTextField
                label="Owner Email"
                name="owner.email"
                required
                disabled={isEditing}
              />

              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "end",
                  columnGap: 2,
                }}
              >
                <Button variant="outlined" onClick={onCancel}>
                  Cancel
                </Button>
                <LoadingButton
                  type="submit"
                  loading={status === ApiStatus.LOADING}
                  variant="contained"
                  color="secondary"
                >
                  {isEditing ? "Update" : "Create"}
                </LoadingButton>
              </Box>
            </Box>
          </FormProvider>
        </Paper>
      </Container>
    </>
  );
};

export default AccountForm;
